import { useRef } from 'react';
import { Link, useRouteContext } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { ArrowLeft, ArrowRight, ArrowUpRight } from 'lucide-react';
import { formatNumber } from '@worldcoo/ui/lib/utils';
import { Image } from '@/routes/-components/image';
import { useTranslation } from '@/lib/i18n';
import { Eyebrow } from '../primitives';
import { buildCampaignHref } from '../portal-href';

export type RecentCampaign = {
	id: string;
	name: string;
	coverImage: string;
	economicGoal?: number;
	collectedCents: number;
	/** Public donor portal of the campaign's merchant, and the campaign's slug inside it. */
	portalId: string;
	slug: string;
	ngo?: { id: string; name?: string; logo?: { location: string } };
};

/**
 * One card's footprint, shared by the real card and the pending one below it. From `lg` the card is an
 * exact third of the visible track (the two 40px gaps taken off first), so the default view is three
 * whole cards and never a sliver of a fourth. Below that it keeps the fixed 340px, where a peeking next
 * card is the affordance that says the row scrolls.
 */
const CARD_SHELL =
	'flex w-[340px] shrink-0 snap-start flex-col overflow-hidden rounded-2xl bg-white shadow-[0_8px_24px_0_rgba(77,33,0,0.06)] lg:w-[calc((100%-5rem)/3)]';

/**
 * The title's box is two lines whether or not the title fills them. `line-clamp-2` already caps it,
 * but without a floor the card's height — and with it the whole section's — depends on how long the
 * newest campaign happens to be named, which is exactly the thing a placeholder cannot know.
 */
const CARD_TITLE = 'line-clamp-2 min-h-14 text-lg font-semibold';

/**
 * The whole card is the link, to the campaign's page in its merchant's portal — a different origin
 * (`correos.worldcoo.com`), so a plain `<a>` rather than a router `<Link>`.
 *
 * Every campaign that reaches this component has a portal: the backend drops the ones whose merchant
 * has none, precisely so this card never has to decide whether it is clickable.
 *
 * It opens in a NEW TAB, as the merchant logo wheel's links and the partner-NGO modal's cards do:
 * this is the marketing site and a campaign page is a different host, so following a card in place
 * replaces the landing page the reader was on — including wherever the carousel had scrolled to.
 * `rel="noopener noreferrer"` alongside, matching every other external link in this route group, and
 * an `sr-only` note because a link that retargets the window should say so.
 */
function CampaignCard(props: { campaign: RecentCampaign; baseUrl: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const campaign = props.campaign;
	const raised = Math.round(campaign.collectedCents / 100);
	const goal = campaign.economicGoal;
	const pct = goal && goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : undefined;

	return (
		<a
			href={buildCampaignHref(props.baseUrl, campaign.portalId, campaign.slug)}
			target="_blank"
			rel="noopener noreferrer"
			className={`${CARD_SHELL} transition-shadow hover:shadow-[0_12px_32px_0_rgba(77,33,0,0.12)]`}
		>
			<div className="relative aspect-[16/10] w-full overflow-hidden">
				<Image
					src={campaign.coverImage}
					alt={campaign.name}
					height={220}
					aspectRatio={16 / 10}
					objectFit="cover"
					className="absolute inset-0 h-full w-full object-cover!"
				/>
			</div>
			<div className="flex flex-1 flex-col gap-4 p-6">
				{campaign.ngo?.logo && (
					<Image
						src={campaign.ngo.logo.location}
						alt={campaign.ngo.name ?? ''}
						height={28}
						aspectRatio={4}
						objectFit="contain"
						className="h-6 w-auto max-w-28 object-contain!"
					/>
				)}
				<h3 className={CARD_TITLE}>{campaign.name}</h3>
				<div className="mt-auto flex flex-col gap-2">
					{/* Whole euros: `formatNumber` defaults to two decimals, and a trailing ",00" on every
					    card is noise at this size. */}
					<p className="text-sm">
						<span className="text-brand-strong font-semibold">{formatNumber(language, raised, 0)} €</span>
						{goal ? (
							<span className="text-ink/50">
								{' '}
								{t`of`} {formatNumber(language, goal, 0)} €
							</span>
						) : null}
					</p>
					{pct !== undefined && (
						<div className="bg-ink/10 h-1 overflow-hidden rounded-full">
							<div className="bg-brand h-full rounded-full" style={{ width: `${pct}%` }} />
						</div>
					)}
					<span className="sr-only">{t`(opens in a new tab)`}</span>
				</div>
			</div>
		</a>
	);
}

/**
 * The card as it looks before its campaign arrives: the same shell, the same 16/10 cover box, the same
 * two-line title and the same footer, drawn as quiet tinted blocks. Every dimension comes from the
 * constants above or from `CampaignCard`'s own classes, which is what makes it measure the same.
 */
function PendingCard() {
	return (
		<article className={CARD_SHELL} aria-hidden>
			<div className="bg-ink/5 aspect-[16/10] w-full" />
			<div className="flex flex-1 flex-col gap-4 p-6">
				<div className="bg-ink/5 h-6 w-28 rounded" />
				<div className={CARD_TITLE}>
					<div className="bg-ink/5 h-4 w-full rounded" />
					<div className="bg-ink/5 mt-2 h-4 w-2/3 rounded" />
				</div>
				<div className="mt-auto flex flex-col gap-2">
					<div className="bg-ink/5 h-5 w-32 rounded" />
					<div className="bg-ink/10 h-1 rounded-full" />
				</div>
			</div>
		</article>
	);
}

/** Three, because that is what the `lg` track shows; a horizontal row's height doesn't depend on it. */
const PENDING_CARDS = [0, 1, 2];

/**
 * "Campañas más recientes de las ONG" — the active campaigns from our system, in a carousel.
 *
 * `campaigns` is OMITTED while they are still streaming in, and the section then renders itself at the
 * size it is about to be: real heading, real CTA, three pending cards. That is what makes
 * `<RecentCampaigns />` this section's own `Suspense` fallback — a placeholder written anywhere else is
 * a second copy of this geometry, and it goes stale the first time a card changes.
 *
 * Loaded and genuinely EMPTY is a different answer: the section disappears, as it always has.
 */
export function RecentCampaigns(props: { campaigns?: RecentCampaign[]; ngoCount?: number }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const { baseUrl } = useRouteContext({ from: '__root__' });
	const scrollerRef = useRef<HTMLDivElement>(null);

	// Measured, not a constant: both the card width and the gap change at `lg`, so a hard-coded step
	// would leave the row parked mid-card at every width but the one it was tuned for. Taking the
	// distance between two cards picks up card + gap in one go, whatever they currently are.
	const scrollByCards = (dir: 1 | -1) => {
		const scroller = scrollerRef.current;
		if (!scroller) return;
		const [first, second] = scroller.children;
		if (!first) return;
		const step = second
			? second.getBoundingClientRect().left - first.getBoundingClientRect().left
			: first.getBoundingClientRect().width;
		scroller.scrollBy({ left: dir * step, behavior: 'smooth' });
	};

	const campaigns = props.campaigns;
	const pending = campaigns === undefined;
	if (campaigns?.length === 0) return null;

	// lingui disallows function calls inside t`` — format the count into a variable first.
	// An em dash while the count is unknown: the sentence keeps its shape — and so its line count, and
	// so this section's height — without any moment where the page states a number that is not real.
	const ngoCountLabel = props.ngoCount === undefined ? '—' : formatNumber(language, props.ngoCount, 0);

	return (
		<section className="mx-auto max-w-[1440px] px-6 py-16 md:py-24 xl:px-10">
			<div className="mx-auto flex max-w-4xl flex-col items-center gap-4 text-center">
				<Eyebrow>{t`Partner NGOs`}</Eyebrow>
				<h2 className="text-3xl font-medium tracking-tight text-balance md:text-5xl">
					{t`The NGOs' most recent campaigns`}
				</h2>
				<p className="text-ink/70 max-w-2xl text-base md:text-lg">
					{t`We collaborate with ${ngoCountLabel} NGOs committed to social, environmental and humanitarian causes. Each one passes a strict selection and audit process to ensure the funds turn into real solutions.`}
				</p>
				<Link
					to="/www/partner-ngos"
					className="bg-brand hover:bg-brand-strong mt-2 flex items-center gap-2 rounded-full py-2.5 pr-2.5 pl-5 font-medium text-black transition-colors"
				>
					{t`See partner NGOs`}
					<span className="bg-ink grid size-6 place-items-center rounded-full text-white">
						<ArrowUpRight className="size-4" />
					</span>
				</Link>
			</div>

			{/* `overflow-x-auto` computes overflow-y to `auto` too, so this padding is the only thing
			    keeping the cards' shadow (8px down, 24px blur) off the clip edge on all four sides.
			    `scroll-px` has to match the side padding, or `snap-start` snaps the first card past it.

			    Below `lg` the track is full-bleed (`-mx-6` cancels the section gutter), the same shape as
			    the hero's swipe rows: the padding then reads as a start inset off the screen edge rather
			    than a box the cards are cut inside, and the row runs off the right as a carousel should.

			    From `lg` it sits back on the section's grid and shows exactly three cards, which is why
			    the gap widens to 40px there. Side padding on a scroller stays VISIBLE, and the fourth
			    card sits `gap - padding` past the clip edge: at an equal 24/24 it landed exactly ON the
			    edge, so the inner 12px of ITS shadow (half of a 24px blur) bled into the gutter and was
			    sliced. 40px of gap puts it 16px clear. The padding cannot shrink instead — the third
			    card needs 12px of its own.

			    `relative` is what makes that clip hold for everything inside: an overflow box only clips the
			    descendants whose containing block it is, and each card's absolutely positioned `sr-only` note
			    otherwise resolves against the body, sits where its card lies along the unscrolled row, and
			    widens the page — with ten campaigns in production, to 4204px. */}
			<div
				ref={scrollerRef}
				className="hide-scrollbar relative -mx-6 mt-8 flex snap-x scroll-px-6 gap-6 overflow-x-auto scroll-smooth px-6 pt-4 pb-8 lg:mx-0 lg:gap-10"
			>
				{pending
					? PENDING_CARDS.map((i) => <PendingCard key={i} />)
					: campaigns.map((campaign) => <CampaignCard key={campaign.id} campaign={campaign} baseUrl={baseUrl} />)}
			</div>

			<div className="mt-2 flex justify-center gap-3">
				<button
					type="button"
					onClick={() => scrollByCards(-1)}
					aria-label={t`Previous`}
					className="border-ink/20 hover:bg-ink/5 grid size-11 place-items-center rounded-full border transition-colors"
				>
					<ArrowLeft className="size-5" />
				</button>
				<button
					type="button"
					onClick={() => scrollByCards(1)}
					aria-label={t`Next`}
					className="border-ink/20 hover:bg-ink/5 grid size-11 place-items-center rounded-full border transition-colors"
				>
					<ArrowRight className="size-5" />
				</button>
			</div>
		</section>
	);
}
